import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonSize, ButtonStyle } from '@novo-electronique/react-button';
import { ITooltipProps, Tooltip } from '@novo-electronique/react-tooltip';

import { FormContext } from './FormContext';
import { FormProvider } from './FormProvider';

export enum FormActionButtonType {
  Submit = 'submit',
  Reset = 'reset',
}

export enum FormActionButtonStyle {
  Default = 'default',
  Primary = 'primary',
  Link = 'link',
}

interface IProps {
  type: FormActionButtonType;
  style?: FormActionButtonStyle;
  small?: boolean;
  children?: string | React.ReactNode | React.ReactNode[];
  disabled?: boolean | ((formik: any) => boolean);
  suppressErrorTooltip?: boolean;
  callback?: () => void;
}

function FormActionButton({
  type,
  style,
  children,
  disabled,
  callback,
  small = false,
  suppressErrorTooltip, // set to true on smaller forms such as the login form where we are sure there will be no page fold.
}: IProps) {
  const { t } = useTranslation();
  const formContext = useContext(FormContext);
  const buttonRef = useRef<HTMLButtonElement>();
  const tooltipUpdateRef = useRef<() => void>();
  const { labelsNamespace } = useContext(FormProvider);

  useEffect(() => {
    // Should force an update of the tooltip if form is changed. Sometime, the tooltip position is not updated correctly.
    if (tooltipComputedProps?.visible && tooltipUpdateRef.current) {
      tooltipUpdateRef.current();
    }
  }, [formContext.values]);

  let buttonStyle: ButtonStyle;
  if ((!style && type === FormActionButtonType.Submit) || style === FormActionButtonStyle.Primary) {
    buttonStyle = ButtonStyle.Primary;
  } else if (style === FormActionButtonStyle.Link) {
    buttonStyle = ButtonStyle.Link;
  }

  const isDisabled = disabled instanceof Function ? (disabled as Function)(formContext.formik) : disabled;
  const buttonIsDisabled =
    (isDisabled !== false && (formContext.disabled || !formContext.formik.dirty)) || isDisabled;

  const tooltipProps = (): Partial<ITooltipProps> => {
    if (type === FormActionButtonType.Reset || suppressErrorTooltip) return { visible: false };

    const touched = getFields(formContext.formik.touched);
    const errors = getFields(formContext.formik.errors, touched);

    // the form will contain errors even if they are not "displayed" since they are displayed
    // only when the fields are touched, we only want to display the tooltip when formik is displaying
    // the errors.
    const touchedErrors = errors.filter((errorKey) => touched.includes(errorKey));

    return {
      visible: touchedErrors.length > 0,
      children: t(`${labelsNamespace}.hasError`, { count: touchedErrors.length }),
    };
  };

  const onClick = (event) => {
    if (type === FormActionButtonType.Reset) {
      event.preventDefault();
      formContext.formik.resetForm();
    }

    if (callback) callback();
  };

  const { children: tooltipChildren, ...tooltipComputedProps } = tooltipProps();
  return (
    <React.Fragment>
      <Button
        ref={buttonRef}
        type={type}
        style={buttonStyle}
        disabled={buttonIsDisabled}
        isLoading={type === FormActionButtonType.Submit && formContext.formik.isSubmitting}
        size={small && ButtonSize.Small}
        onClick={onClick}
      >
        {children
          ? children
          : t(
              type === FormActionButtonType.Submit
                ? `${labelsNamespace}.submit`
                : `${labelsNamespace}.cancel`,
            )}
      </Button>
      <Tooltip {...tooltipComputedProps} targetRef={buttonRef} forceUpdateRef={tooltipUpdateRef}>
        {tooltipChildren}
      </Tooltip>
    </React.Fragment>
  );
}

const getFields = (obj: { [key: string]: any }, props: string[] = [], parentKey?: string): string[] => {
  const getName = (key: string, parentKey?: string): string => (parentKey ? `${parentKey}.${key}` : key);

  return Object.keys(obj).reduce((arr, key) => {
    const name = getName(key, parentKey);

    return Array.isArray(obj[key]) || typeof obj[key] !== 'object' || props.includes(name)
      ? [...arr, name]
      : [...arr, ...getFields(obj[key], props, name)];
  }, []);
};

export default FormActionButton;
